<main id="main-content" class="ds-product-page ds-product-page--narrow">
  <p class="eyebrow">{{ 'consumer.eyebrow' | t }}</p>
  <h1>{{ 'consumer.title' | t }}</h1>
  <p class="lede">{{ 'consumer.lede' | t }}</p>

  @if (error()) {
    <p class="error" role="alert">{{ error() }}</p>
  }

  @if (!user()) {
    <form (ngSubmit)="login()">
      <h2>{{ 'common.developmentLogin' | t }}</h2>
      <p class="notice">{{ 'consumer.noPayment' | t }}</p>
      <label for="consumer-email">{{ 'common.email' | t }}</label>
      <input dsInput id="consumer-email" type="email" [formControl]="email" autocomplete="email" />
      <button dsButton type="submit" [disabled]="busy() || email.invalid">
        {{ 'common.continue' | t }}
      </button>
    </form>
  } @else if (!progress()) {
    <section dsCard class="card">
      <h2>{{ 'consumer.choose' | t }}</h2>
      @for (assessment of assessments(); track assessment.id) {
        <article class="choice">
          <div>
            <strong>{{ assessment.name }}</strong
            ><br /><span
              >{{ 'common.version' | t }} {{ assessment.versionNumber }} ·
              {{ assessment.language }}</span
            >
          </div>
          <button dsButton type="button" (click)="start(assessment.id)" [disabled]="busy()">
            {{ 'consumer.start' | t }}
          </button>
        </article>
      }
    </section>
  } @else if (progress()?.currentItem; as item) {
    <section class="card item" [attr.aria-labelledby]="'item-' + item.sequence">
      <p class="muted">Item {{ item.sequence }} · {{ item.domain }}</p>
      <h2 [id]="'item-' + item.sequence">{{ item.prompt }}</h2>
      @if (item.safeSvg) {
        <div class="visual" [innerHTML]="item.safeSvg"></div>
      }
      <div class="options">
        @for (option of item.options; track option.code) {
          <button dsButton type="button" (click)="answer(option.code)" [disabled]="busy()">
            <span>{{ option.code }}</span
            >{{ option.text }}
          </button>
        }
      </div>
      <p class="muted">{{ 'consumer.saved' | t }}</p>
    </section>
  } @else if (progress()?.preview; as preview) {
    <section class="card completion" aria-live="polite">
      <h2>{{ 'consumer.preview' | t }}</h2>
      <p class="range">{{ preview.rangeLabel }}</p>
      <p>
        {{ 'consumer.previewTextBefore' | t }} {{ preview.answeredItems }}
        {{ 'consumer.previewTextAfter' | t }}
      </p>
      @if (!order()) {
        <div class="checkout-consent">
          <label>
            <input
              dsCheckbox
              type="checkbox"
              [formControl]="purchaseConsent"
              [attr.aria-describedby]="
                purchaseConsent.invalid && purchaseConsent.touched ? 'checkout-consent-error' : null
              "
            />
            <span
              >I expressly request immediate delivery of the digital assessment report before the
              end of the 14-day withdrawal period. I understand that once digital delivery begins, I
              lose my right to withdraw from this digital-content purchase.</span
            >
          </label>
          <label>
            <input
              dsCheckbox
              type="checkbox"
              [formControl]="termsConsent"
              [attr.aria-describedby]="
                termsConsent.invalid && termsConsent.touched ? 'checkout-consent-error' : null
              "
            />
            <span>
              I have read and agree to the
              <a href="/en/legal/terms-and-conditions/">Terms &amp; Conditions</a>.
            </span>
          </label>
          <p>
            Read the
            <a href="/en/legal/digital-content-eu-withdrawal-rights/">EU Withdrawal Rights Notice</a
            >, <a href="/en/legal/refund-policy/">Refund Policy</a>, and
            <a href="/en/legal/privacy-policy/">Privacy Policy</a>.
          </p>
          @if (
            (purchaseConsent.invalid && purchaseConsent.touched) ||
            (termsConsent.invalid && termsConsent.touched)
          ) {
            <p id="checkout-consent-error" class="field-error" role="alert">
              Both explicit consent selections are required before purchase.
            </p>
          }
        </div>
        @for (product of products(); track product.id) {
          <button
            dsButton
            type="button"
            (click)="buy(product.id)"
            [disabled]="busy() || purchaseConsent.invalid || termsConsent.invalid"
          >
            {{ 'consumer.unlock' | t }} {{ product.name }} — {{ product.priceMinorUnits / 100 }}
            {{ product.currency }}
          </button>
        }
      } @else if (order()?.status !== 'Paid') {
        <aside class="dev-payment" [attr.aria-label]="'consumer.paymentSimulator' | t">
          <strong>{{ 'consumer.paymentSimulator' | t }}</strong>
          <p>{{ 'consumer.paymentText' | t }}</p>
          <button dsButton type="button" (click)="completeDevelopmentPayment()" [disabled]="busy()">
            {{ 'consumer.simulatePayment' | t }}
          </button>
        </aside>
      } @else {
        <p class="success">{{ 'consumer.paymentVerified' | t }}</p>
      }
    </section>
  }

  @if (reports().length) {
    <section dsCard class="card">
      <h2>{{ 'consumer.history' | t }}</h2>
      @for (report of reports(); track report.id) {
        <article class="choice">
          <div>
            <strong>{{ report.assessmentVersion }}</strong
            ><br /><span
              >{{ report.answeredItems }} {{ 'common.items' | t }} ·
              {{ report.generatedAtUtc }}</span
            >
          </div>
          @if (report.unlocked) {
            <nav [attr.aria-label]="'consumer.formats' | t">
              <a [href]="reportUrl(report.id, 'html')">{{ 'consumer.accessibleHtml' | t }}</a
              ><a [href]="reportUrl(report.id, 'pdf')">{{ 'common.pdf' | t }}</a>
            </nav>
          } @else {
            <span>{{ 'common.locked' | t }}</span>
          }
        </article>
      }
    </section>
  }

  @if (user()) {
    <section dsCard class="card" aria-labelledby="privacy-heading">
      <h2 id="privacy-heading">{{ 'consumer.privacy' | t }}</h2>
      <p>{{ 'consumer.privacyText' | t }}</p>
      <div class="privacy-actions">
        <button dsButton type="button" (click)="requestPrivacy('Export')" [disabled]="busy()">
          {{ 'consumer.export' | t }}</button
        ><button dsButton type="button" (click)="requestPrivacy('Deletion')" [disabled]="busy()">
          {{ 'consumer.deletion' | t }}</button
        ><button dsButton type="button" (click)="requestPrivacy('Dispute')" [disabled]="busy()">
          {{ 'consumer.dispute' | t }}
        </button>
      </div>
      @if (privacyRequests().length) {
        <ul>
          @for (request of privacyRequests(); track request.id) {
            <li>
              <strong>{{ request.type }}</strong> — {{ request.status }}
            </li>
          }
        </ul>
      }
    </section>
  }
</main>
